import { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Search, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useDebounce } from '../hooks/useDebounce';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { SearchSuggestions } from './SearchSuggestions';
import { SearchFilters } from './SearchFilters';

export function SearchBar({
  onSearch,
  placeholder = 'Search posts...',
  categories = [],
  getSuggestions,
  defaultTerm = '',
}) {
  const [searchTerm, setSearchTerm] = useState(defaultTerm);

  useEffect(() => {
    setSearchTerm(defaultTerm);
  }, [defaultTerm]);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');

  const { history, addToHistory, removeFromHistory } = useSearchHistory();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    let isMounted = true;

    const fetchSuggestions = async () => {
      if (!debouncedSearchTerm || !getSuggestions) return;
      
      setIsLoading(true);
      try {
        const results = await getSuggestions(debouncedSearchTerm);
        if (isMounted) {
          setSuggestions(results);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchSuggestions();
    return () => { isMounted = false; };
  }, [debouncedSearchTerm, getSuggestions]);

  useEffect(() => {
    let isMounted = true;

    const performSearch = async () => {
      if (!debouncedSearchTerm) return;

      setIsLoading(true);
      try {
        await onSearch({
          term: debouncedSearchTerm,
          category: selectedCategory,
          dateRange,
          sortBy
        });
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    performSearch();
    return () => { isMounted = false; };
  }, [debouncedSearchTerm, selectedCategory, dateRange, sortBy, onSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setShowSuggestions(false);
    addToHistory(searchTerm);
    
    await onSearch({
      term: searchTerm,
      category: selectedCategory,
      dateRange,
      sortBy
    });
  };

  const handleChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    onSearch({ term: '', category: selectedCategory, dateRange, sortBy });
  };

  const handleSuggestionSelect = (value) => {
    setSearchTerm(value);
    setShowSuggestions(false);
    addToHistory(value);
    onSearch({
      term: value,
      category: selectedCategory,
      dateRange,
      sortBy
    });
  };

  return (
    <div className="w-full max-w-2xl">
      <form 
        onSubmit={handleSubmit} 
        className="relative w-full"
        role="search"
        aria-label="Search posts"
      >
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Search 
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" 
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleChange}
              className="pl-9 pr-4"
              aria-label="Search input"
              onFocus={() => setShowSuggestions(true)}
            />
            {isLoading && (
              <div 
                role="status" 
                aria-label="Loading suggestions"
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <Loader2 
                  className="h-4 w-4 animate-spin text-muted-foreground" 
                  aria-hidden="true"
                />
                <span className="sr-only">Loading suggestions</span>
              </div>
            )}
          </div>
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="absolute right-20 top-1/2 -translate-y-1/2 h-8 w-8 px-0"
              aria-label="Clear search"
            >
              ×
            </Button>
          )}
          <Button 
            type="submit" 
            variant="secondary"
            aria-label="Submit search"
            disabled={isLoading}
          >
            Search
          </Button>
        </div>

        {showSuggestions && (
          <SearchSuggestions
            suggestions={suggestions}
            history={history}
            isLoading={isLoading}
            onSelect={handleSuggestionSelect}
            onClearHistory={removeFromHistory}
          />
        )}
      </form>

      <div data-testid="category-select-wrapper">
        <SearchFilters
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          sortBy={sortBy}
          onSortChange={setSortBy}
          isExpanded={isFiltersExpanded}
          onToggle={() => setIsFiltersExpanded(!isFiltersExpanded)}
        />
      </div>
    </div>
  );
}
